

import * as React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, Check, Eye, EyeOff } from 'lucide-react';
import { api, ApiError, AdminUser, AdminUserRole, AdminUserStatus } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

export default function AdminEditPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { user: currentUser } = useAuth();
  const adminId = params.id as string;

  const [admin, setAdmin] = React.useState<AdminUser | null>(null);
  const [roles, setRoles] = React.useState<AdminUserRole[]>([]);
  const [statuses, setStatuses] = React.useState<AdminUserStatus[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingOptions, setIsLoadingOptions] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);

  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    password: '',
    role: 'general-admin',
    status: 'active',
  });

  const isCurrentUser = currentUser?.id === adminId;

  React.useEffect(() => {
    const loadAdmin = async () => {
      try {
        setIsLoading(true);
        const data = await api.getAdminUser(adminId);
        setAdmin(data);
        setFormData({
          name: data.name,
          email: data.email,
          password: '',
          role: data.role,
          status: data.status,
        });
      } catch (err) {
        const apiError = err as ApiError;
        setError(apiError.message || 'Erro ao carregar administrador');
      } finally {
        setIsLoading(false);
      }
    };
    loadAdmin();
  }, [adminId]);

  React.useEffect(() => {
    const loadOptions = async () => {
      try {
        const [rolesData, statusesData] = await Promise.all([
          api.getAdminUserRoles(),
          api.getAdminUserStatuses(),
        ]);
        setRoles(rolesData);
        setStatuses(statusesData);
      } catch (err) {
        console.error('Erro ao carregar opcoes:', err);
      } finally {
        setIsLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updateData: Record<string, string> = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        status: formData.status,
      };

      // Apenas inclui senha se foi preenchida
      if (formData.password) {
        updateData.password = formData.password;
      }

      await api.updateAdminUser(adminId, updateData);
      setSuccessMessage('Administrador atualizado com sucesso!');
      setTimeout(() => {
        navigate('/admins');
      }, 1500);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao atualizar administrador');
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
          <p className="text-gray-500">Carregando administrador...</p>
        </div>
      </div>
    );
  }

  if (error && !admin) {
    return (
      <div className="space-y-8">
        <div className="flex items-center gap-4">
          <Link
            to="/admins"
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Editar Administrador</h1>
          </div>
        </div>

        <div className="bg-red-50 rounded-2xl border border-red-100 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">
                Erro ao carregar administrador
              </h3>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
            <Link
              to="/admins"
              className="px-4 py-2 rounded-xl bg-red-100 text-red-700 text-sm font-medium hover:bg-red-200 transition-colors"
            >
              Voltar
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/admins"
          className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Editar Administrador</h1>
          <p className="text-gray-500 mt-1">
            Atualize os dados do administrador {admin?.name}
            {isCurrentUser && (
              <span className="ml-2 text-[#0057FF] text-sm">(Voce)</span>
            )}
          </p>
        </div>
      </div>

      {/* Success Alert */}
      {successMessage && (
        <div className="bg-emerald-50 rounded-2xl border border-emerald-100 p-4">
          <div className="flex items-center gap-3">
            <Check className="h-5 w-5 text-emerald-500 shrink-0" />
            <p className="text-emerald-700 text-sm">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 rounded-2xl border border-red-100 p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Informacoes Basicas</h2>
            <p className="text-sm text-gray-500 mt-1">Dados de acesso do administrador</p>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Joao Silva"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="joao@exemplo.com"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Nova Senha</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  minLength={8}
                  value={formData.password}
                  onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  placeholder="Deixe em branco para manter a senha atual"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-12 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-400">Se preenchida, a senha deve ter no minimo 8 caracteres</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Role</label>
                {isLoadingOptions ? (
                  <div className="flex items-center gap-2 py-3">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    <span className="text-sm text-gray-400">Carregando...</span>
                  </div>
                ) : (
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                  >
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Status</label>
                {isLoadingOptions ? (
                  <div className="flex items-center gap-2 py-3">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    <span className="text-sm text-gray-400">Carregando...</span>
                  </div>
                ) : (
                  <>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                      disabled={isCurrentUser}
                      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
                    >
                      {statuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                    {isCurrentUser && (
                      <p className="text-xs text-amber-600">Voce nao pode alterar seu proprio status</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Link
            to="/admins"
            className="px-6 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#0057FF] to-[#7C3AED] text-white font-medium text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#0057FF]/25 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Alteracoes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
